import isEqual from 'lodash/isEqual';
import Assignees from '@/constants/Assignees';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import DateType from '@/constants/DateType';
import FilterOperator from '@/constants/forms/FilterOperator';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import type SearchConstituent from '@/types/api/SearchConstituent';
import type LabelSelectValue from '@/types/LabelSelectValue';
import { formatDateToString } from '@/utils/dates';
import TemporaryFilterField from '../constants/TemporaryFilterField';
import type ConstituentFilterFormData from '../types/ConstituentFilterFormData';

const itemIsNotUndefined = (item: unknown) => item !== undefined;

const getPairedValue = <X, Y = X>(
  value: unknown[],
  operator: FilterOperator,
  mapFunc: (x: X) => Y,
  validatorFunc: (x: unknown) => boolean,
): Y | Y[] | null => {
  if (operator === FilterOperator.BETWEEN && value.every(validatorFunc)) {
    return (value as X[]).map(mapFunc);
  }
  if (operator === FilterOperator.GREATER_THAN && validatorFunc(value[0])) {
    return mapFunc(value[0] as X);
  }
  if (operator === FilterOperator.LESS_THAN && validatorFunc(value[1])) {
    return mapFunc(value[1] as X);
  }
  return null;
};

const getPairedValueOperator = (value: unknown[]): FilterOperator => {
  if (value.every(itemIsNotUndefined)) {
    return FilterOperator.BETWEEN;
  }
  if (itemIsNotUndefined(value[0])) {
    return FilterOperator.GREATER_THAN;
  }
  return FilterOperator.LESS_THAN;
};

const mapFormDataToFilters = (
  defaultValues: ConstituentFilterFormData,
  formData: ConstituentFilterFormData,
) =>
  Object.entries(formData).reduce<AdvancedConstituentSearchFilter[]>(
    (arr, [key, val]) => {
      // Omit any filters whose values are null-ish or unchanged from the default
      if (
        val === null ||
        val === undefined ||
        !(key in defaultValues) ||
        isEqual(val, defaultValues[key as keyof ConstituentFilterFormData])
      ) {
        return arr;
      }

      if (
        key === ConstituentSearchFilterField.DONATION_DATE_TYPE &&
        val === DateType.CUSTOM_DATES
      ) {
        return arr;
      }

      let field = key;
      let operator: FilterOperator = Array.isArray(val)
        ? FilterOperator.ANY
        : FilterOperator.IS;
      let value: unknown = val;

      switch (key) {
        case ConstituentSearchFilterField.NAME_EMAIL_PHONE:
        case ConstituentSearchFilterField.CITY:
          operator = FilterOperator.CONTAINS;
          break;

        case ConstituentSearchFilterField.IWAVE_CAPACITY_SCORE:
        case ConstituentSearchFilterField.IWAVE_SCORE:
        case ConstituentSearchFilterField.IWAVE_AFFINITY_SCORE:
        case ConstituentSearchFilterField.IWAVE_PLANNED_GIVING_SCORE:
        case ConstituentSearchFilterField.IWAVE_PROPENSITY_SCORE:
          operator = FilterOperator.IS;
          break;

        // Map any temporary fields to their API equivalent
        case TemporaryFilterField.ASSIGNEE:
          if (val === Assignees.ALL) {
            return arr;
          }
          if (val === Assignees.ASSIGNED || val === Assignees.UNASSIGNED) {
            field = ConstituentSearchFilterField.USER_IDS;
            operator =
              val === Assignees.ASSIGNED
                ? FilterOperator.IS_NOT_NULL
                : FilterOperator.IS_NULL;
            value = undefined;
          } else {
            field = ConstituentSearchFilterField.USER_IDS;
            operator = FilterOperator.ANY;
            value = [val];
          }
          break;

        case TemporaryFilterField.CONNECTION_CONSTITUENT:
          field = ConstituentSearchFilterField.CONNECTION_CONSTITUENT_IDS;
          value = (val as SearchConstituent).constituentId;
          operator = FilterOperator.IS;
          break;

        case TemporaryFilterField.CONSTITUENT_LABELS:
        case TemporaryFilterField.CONTRIBUTION_LABELS:
          field =
            key === TemporaryFilterField.CONSTITUENT_LABELS
              ? ConstituentSearchFilterField.CONSTITUENT_LABEL_IDS
              : ConstituentSearchFilterField.DONATION_LABEL_IDS;
          value = (val as LabelSelectValue[])
            .map(({ labelId }) => labelId)
            .filter(Boolean);
          break;

        case TemporaryFilterField.DONATION_AMOUNT_RANGE:
          if (!Array.isArray(val)) {
            return arr;
          }
          field = ConstituentSearchFilterField.DONATION_AMOUNT;
          operator = getPairedValueOperator(val);
          value = getPairedValue<number>(
            val,
            operator,
            (x) => x * 100,
            (x) => typeof x === 'number',
          );
          break;

        case TemporaryFilterField.DONATION_HOURS_RANGE:
          if (!Array.isArray(val)) {
            return arr;
          }
          field = ConstituentSearchFilterField.DONATION_MINUTES;
          operator = getPairedValueOperator(val);
          value = getPairedValue<number>(
            val,
            operator,
            (x) => x * 60,
            (x) => typeof x === 'number',
          );
          break;

        case TemporaryFilterField.BORN_DATE_RANGE:
          if (!Array.isArray(val)) {
            return arr;
          }
          field = ConstituentSearchFilterField.DOB;
          operator = getPairedValueOperator(val);
          value = getPairedValue<Date, string>(
            val,
            operator,
            formatDateToString,
            (x) => x instanceof Date,
          );
          break;

        case TemporaryFilterField.DONATION_DATE_RANGE:
          if (
            !Array.isArray(val) ||
            formData[ConstituentSearchFilterField.DONATION_DATE_TYPE] !==
              DateType.CUSTOM_DATES
          ) {
            return arr;
          }

          field = ConstituentSearchFilterField.DONATION_DATE;
          operator = getPairedValueOperator(val);
          value = getPairedValue<Date, string>(
            val,
            operator,
            formatDateToString,
            (x) => x instanceof Date,
          );
          break;

        case TemporaryFilterField.CUSTOM_FIELDS:
          return [
            ...arr,
            ...Object.entries(val).reduce<AdvancedConstituentSearchFilter[]>(
              (customArr, [customKey, item]) => {
                const { value: customVal, fieldType } = item;
                if (
                  (Array.isArray(customVal) && !customVal.length) ||
                  customVal === undefined ||
                  customVal === null ||
                  customVal === ''
                ) {
                  return customArr;
                }
                let customOperator = FilterOperator.IS;
                let customValue = customVal;
                if (fieldType === CustomFieldTypes.DATE_PICKER) {
                  if (!Array.isArray(customVal)) {
                    return customArr;
                  }
                  customOperator = getPairedValueOperator(customVal);
                  customValue = getPairedValue<Date, string>(
                    customVal,
                    customOperator,
                    formatDateToString,
                    (x) => x instanceof Date,
                  );
                  if (customValue === null) {
                    return customArr;
                  }
                } else if (
                  fieldType === CustomFieldTypes.TEXT_MULTI ||
                  fieldType === CustomFieldTypes.TEXT_SINGLE
                ) {
                  customOperator = FilterOperator.CONTAINS;
                } else if (fieldType === CustomFieldTypes.CHECKBOX) {
                  customOperator = customVal
                    ? FilterOperator.IS_NOT_NULL
                    : FilterOperator.IS_NULL;
                  customValue = undefined;
                } else {
                  customValue = customVal;
                  customOperator = Array.isArray(customVal)
                    ? FilterOperator.ANY
                    : FilterOperator.IS;
                }
                const filter: AdvancedConstituentSearchFilter = {
                  field: customKey,
                  isCustomField: true,
                  operator: customOperator,
                };
                if (itemIsNotUndefined(customValue)) {
                  filter.value = customValue;
                }

                return [...customArr, filter];
              },
              [],
            ),
          ];
        default:
          break;
      }

      const item: AdvancedConstituentSearchFilter = { field, operator };
      if (value === null) {
        return arr;
      }
      if (itemIsNotUndefined(value)) {
        item.value = value;
      }

      return [...arr, item];
    },
    [],
  );

export default mapFormDataToFilters;
